import type { MediosDelAlta } from '../../api/alta'
import type { TipoDeWeb } from '../../api/tipos'

/**
 * Lo que el asistente va recogiendo (docs/alta-de-web-con-plantillas.md §8). Se guarda
 * en el navegador (`localStorage`, unos días) por si se recarga o se cierra a medias, y el
 * paso en que va está en la dirección (`?paso=`), así que «Atrás» vuelve al paso anterior
 * y no fuera del asistente. Los ficheros no se pueden guardar ahí: al volver hay que
 * elegirlos otra vez, y se dice.
 *
 * Nada de esto llega al núcleo hasta «Crear mi web»: el asistente no crea nada a
 * medias, salvo las imágenes, los documentos y el Excel de productos, que se suben justo
 * después de crear la web porque necesitan una a la que pertenecer. Si un documento no se
 * puede leer, el alta espera ahí (`AltaEnEspera`, más abajo).
 */
export const PASOS = ['tipo', 'plantilla', 'marca', 'materiales', 'paginas', 'productos', 'resumen'] as const
export type Paso = (typeof PASOS)[number]

/** Los pasos que toca recorrer: «Productos» solo en una tienda. */
export const pasosPara = (tipo: TipoDeWeb | null): Paso[] => PASOS.filter((p) => p !== 'productos' || tipo === 'tienda')

/**
 * El paso que dice la dirección, si es uno de los que toca y se puede llegar a él: sin tipo
 * elegido no se salta a «Resumen» escribiendo la dirección a mano. Si no, el primero.
 */
export const pasoDeLaDireccion = (valor: null | string, pasos: readonly Paso[], alcanzable: (p: Paso) => boolean): Paso => {
  const pedido = pasos.find((p) => p === valor)
  if (!pedido) return pasos[0] ?? 'tipo'
  // Hasta el pedido, todos los de antes tienen que poder dejarse atrás.
  for (const p of pasos) {
    if (p === pedido) return pedido
    if (!alcanzable(p)) return p
  }
  return pasos[0] ?? 'tipo'
}

/** A qué paso vuelve el asistente cuando el núcleo rechaza un campo. */
export const pasoDelCampo = (campo: unknown): Paso | null => {
  const nombre = String(campo ?? '')
  if (nombre === 'tipo') return 'tipo'
  if (nombre === 'plantilla') return 'plantilla'
  if (nombre === 'paginas') return 'paginas'
  if (/^(nombre|slug|dominio|dominios|marca|contacto|tienda|fiscal)/.test(nombre)) return 'marca'
  return null
}

type ErrorConDatos = { message?: string; datos?: Record<string, unknown> }

/**
 * El mensaje que hay que enseñar de un error del núcleo. Cuando lo rechaza la propia
 * colección, arriba viene el genérico de Payload («The following field is invalid: nombre»)
 * y el bueno va dentro, en `data.errors` (auditoría del panel, 29-09-2026, A2).
 */
export const mensajeDelFallo = (fallo: unknown, porOmision = 'Algo no ha ido bien. Vuelve a intentarlo.'): string => {
  const e = fallo as ErrorConDatos
  const dentro = Array.isArray(e?.datos?.errors) ? (e.datos.errors as Array<{ message?: unknown }>).map((x) => x?.message).find((m): m is string => typeof m === 'string' && m.trim() !== '') : undefined
  return dentro ?? (typeof e?.message === 'string' && e.message.trim() ? e.message : porOmision)
}

/** El campo que el núcleo ha rechazado: el que dice el asistente (`campo`) o el de la colección (`errors[0].path`). */
export const campoDelFallo = (fallo: unknown): null | string => {
  const datos = (fallo as ErrorConDatos)?.datos
  if (typeof datos?.campo === 'string') return datos.campo
  const ruta = Array.isArray(datos?.errors) ? (datos.errors as Array<{ path?: unknown }>)[0]?.path : undefined
  return typeof ruta === 'string' ? ruta : null
}

export const ROTULOS: Record<Paso, string> = {
  tipo: 'Tipo de web',
  plantilla: 'Plantilla',
  marca: 'Tu marca',
  materiales: 'Materiales',
  paginas: 'Páginas',
  productos: 'Productos',
  resumen: 'Resumen',
}

export type Datos = {
  tipo: TipoDeWeb | null
  plantilla: null | string
  nombre: string
  slug: string
  slugTocado: boolean
  dominio: string
  marca: { primario: string; secundario: string; acento: string; tipografia: 'clasica' | 'moderna' | 'sistema' }
  /** `horario`: como lo escriba el cliente; lo pinta la plantilla que lo usa y va al SEO de negocio local. */
  contacto: { telefono: string; correo: string; direccion: string; horario: string }
  /** Para los textos legales: razón social, identificación fiscal y domicilio. */
  fiscal: { razonSocial: string; identificacion: string; direccion: string }
  tienda: { modo: 'catalogo' | 'tienda'; consulta: 'ambos' | 'formulario' | 'whatsapp'; moneda: string }
  brief: string
  /** Claves de las páginas elegibles que el cliente marcó. Las obligatorias no se guardan: van siempre. */
  paginas: string[]
  /** Los servicios, uno por línea (informativa con página de servicios). */
  servicios: string
}

export const DATOS_INICIALES: Datos = {
  tipo: null,
  plantilla: null,
  nombre: '',
  slug: '',
  slugTocado: false,
  dominio: '',
  marca: { primario: '', secundario: '', acento: '', tipografia: 'sistema' },
  contacto: { telefono: '', correo: '', direccion: '', horario: '' },
  fiscal: { razonSocial: '', identificacion: '', direccion: '' },
  tienda: { modo: 'tienda', consulta: 'ambos', moneda: 'PEN' },
  brief: '',
  paginas: [],
  servicios: '',
}

/** Un fichero elegido, con su texto alternativo. */
export type Imagen = { fichero: File; alt: string }

export type Ficheros = {
  logo: Imagen | null
  logoEnOscuro: Imagen | null
  favicon: Imagen | null
  /** Por clave del brief: una imagen para las claves de tipo `imagen`, varias para `imagenes`. */
  imagenes: Record<string, Imagen[]>
  /** El PDF, el Word o el texto con lo que ofrece: hasta tres. */
  documentos: File[]
  /** Imágenes sin sitio fijo en la receta: van a la biblioteca, para ponerlas después en las páginas. */
  sueltas: Imagen[]
}

export const FICHEROS_INICIALES: Ficheros = { logo: null, logoEnOscuro: null, favicon: null, imagenes: {}, documentos: [], sueltas: [] }

/** Si hay algún fichero elegido: es lo que se pierde al cerrar o recargar, y lo que hace preguntar antes de salir. */
export const hayFicherosElegidos = (f: Ficheros, conExcel = false): boolean =>
  conExcel || Boolean(f.logo || f.logoEnOscuro || f.favicon) || f.documentos.length > 0 || f.sueltas.length > 0 || Object.values(f.imagenes).some((l) => l.length > 0)

/**
 * Lo que va a `continuar` a partir de lo subido: cada hueco con sus imágenes en el orden que
 * eligió el cliente, y las sueltas en el suyo. Lo que no subió no desplaza a lo demás.
 */
export const mediosDeLasSubidas = (o: { logo?: number; logoEnOscuro?: number; favicon?: number; porClave: Record<string, Array<number | undefined>>; sueltas: Array<number | undefined> }): MediosDelAlta => {
  const medios: MediosDelAlta = { imagenes: {} }
  if (o.logo) medios.logo = o.logo
  if (o.logoEnOscuro) medios.logoEnOscuro = o.logoEnOscuro
  if (o.favicon) medios.favicon = o.favicon
  for (const [clave, ids] of Object.entries(o.porClave)) {
    const buenos = ids.filter((n): n is number => Number.isInteger(n))
    if (buenos.length > 0) medios.imagenes![clave] = buenos
  }
  const sueltas = [...new Set(o.sueltas.filter((n): n is number => Number.isInteger(n)))]
  if (sueltas.length > 0) medios.sueltas = sueltas
  return medios
}

export const MAXIMO_DE_IMAGENES_SUELTAS = 40

/**
 * Corre las tareas de a `limite` a la vez, en orden de llegada. Para subir treinta
 * imágenes: tres en paralelo aprovechan la conexión sin ahogar al núcleo.
 *
 * Una que falle **para de verdad**: `Promise.all` rechaza con el primer fallo, pero los
 * otros obreros seguían vaciando la lista y mandando peticiones después de que quien
 * llamó ya se hubiera ido, dejando medios huérfanos que nadie podía relacionar con nada.
 */
export const enParalelo = async <T>(elementos: T[], limite: number, hacer: (elemento: T, indice: number) => Promise<void>): Promise<void> => {
  let siguiente = 0
  let roto: unknown = null
  const obreros = Array.from({ length: Math.max(1, Math.min(limite, elementos.length)) }, async () => {
    while (siguiente < elementos.length && roto === null) {
      const indice = siguiente++
      try {
        await hacer(elementos[indice]!, indice)
      } catch (fallo) {
        roto ??= fallo
        return
      }
    }
  })
  await Promise.all(obreros)
  if (roto !== null) throw roto
}

/**
 * Cómo se reconoce el mismo fichero elegido dos veces: el navegador da un `File` nuevo
 * cada vez que se elige, así que no vale compararlos; nombre, tamaño y fecha, sí.
 */
export const claveDeFichero = (fichero: File): string => `${fichero.name}\0${fichero.size}\0${fichero.lastModified}`

/**
 * Las subidas de verdad: un fichero elegido para varios huecos (el logo y el favicon, un
 * banner que también va suelto) se sube **una vez** y su medio va a todos. El 27-09-2026
 * un cliente eligió la misma imagen para dos huecos, las dos subidas salieron a la vez
 * con el mismo nombre y la segunda se perdió; además, cada copia gastaba cuota. Cada
 * grupo lleva la imagen (con el texto alternativo) del primer hueco en que se eligió
 * —o del primero que lo tenga escrito, si ese lo dejó vacío: si no, se subiría con el
 * nombre del fichero como texto alternativo y se perdería el que escribió el cliente—, y
 * los grupos salen en el orden en que aparecen.
 */
export const agruparPorFichero = <T extends { imagen: Imagen }>(tareas: T[]): Array<{ imagen: Imagen; tareas: T[] }> => {
  const grupos = new Map<string, { imagen: Imagen; tareas: T[] }>()
  for (const tarea of tareas) {
    const clave = claveDeFichero(tarea.imagen.fichero)
    const grupo = grupos.get(clave)
    if (!grupo) {
      grupos.set(clave, { imagen: tarea.imagen, tareas: [tarea] })
      continue
    }
    grupo.tareas.push(tarea)
    if (!grupo.imagen.alt.trim() && tarea.imagen.alt.trim()) grupo.imagen = tarea.imagen
  }
  return [...grupos.values()]
}

/**
 * El nombre del documento con el texto que pega el cliente cuando el suyo no se ha podido
 * leer: `folleto.pdf` → `folleto (texto pegado).txt`. Así, en el brief, se sabe de dónde
 * salió.
 */
export const nombreDelTextoPegado = (original: string): string => {
  const base = original.replace(/\.[a-z0-9]{1,5}$/i, '').trim() || 'documento'
  return `${base} (texto pegado).txt`
}

/** Lo mínimo que el núcleo acepta como documento: con menos, «casi no tiene texto que leer». */
export const MINIMO_DE_TEXTO_PEGADO = 40

/** Un documento del brief que el núcleo no ha podido leer, con lo que dijo (que dice qué hacer). */
export type DocumentoSinLeer = { id: number; nombre: string; mensaje: string }

/**
 * El alta que **espera** a que el cliente decida qué hacer con los documentos que no se han
 * podido leer. La web ya existe y el alta sigue en «materiales», así que el núcleo todavía
 * admite otro fichero, o su texto pegado (como un `.txt`), y se continúa solo cuando el
 * cliente lo dice. Si se continuara sin preguntar, el mensaje del núcleo —«pega su texto o
 * súbelo en otro formato»— pediría algo que ya no se puede hacer: con el alta en marcha, el
 * núcleo no admite más documentos.
 *
 * Se guarda en `sessionStorage`: recargar la página no deja la web a medias sin saber qué
 * faltaba. El Excel de productos ya está en el núcleo (se sube antes que los documentos), así
 * que no se pierde.
 */
export type AltaEnEspera = {
  sitio: number
  /** Las imágenes ya subidas, para dárselas al alta al continuar. */
  medios: MediosDelAlta
  /** Lo que ya no salió bien al crear la web (una imagen que no subió): van a Progreso. */
  avisos: string[]
  documentos: DocumentoSinLeer[]
}

/** Sin ese documento: se leyó al segundo intento, o el cliente lo quita. */
export const sinDocumento = (espera: AltaEnEspera, id: number): AltaEnEspera => ({ ...espera, documentos: espera.documentos.filter((d) => d.id !== id) })

/** El documento sigue sin leerse, con lo último que dijo el núcleo. */
export const conMensaje = (espera: AltaEnEspera, id: number, mensaje: string): AltaEnEspera => ({
  ...espera,
  documentos: espera.documentos.map((d) => (d.id === id ? { ...d, mensaje } : d)),
})

const claveDeLaEspera = (espacio: number) => `wixy-alta-${espacio}-espera`

const esEntero = (valor: unknown): valor is number => typeof valor === 'number' && Number.isInteger(valor) && valor > 0

/** La espera guardada, si la hay y tiene la forma de una; si no, nada (y el asistente empieza como siempre). */
export const leerEspera = (espacio: number): AltaEnEspera | null => {
  try {
    const crudo = sessionStorage.getItem(claveDeLaEspera(espacio))
    if (!crudo) return null
    const e = JSON.parse(crudo) as Partial<AltaEnEspera>
    const documentos = Array.isArray(e.documentos) ? e.documentos : null
    if (!esEntero(e.sitio) || !e.medios || typeof e.medios !== 'object' || !Array.isArray(e.avisos) || !documentos) return null
    if (!documentos.every((d) => d && Number.isInteger(d.id) && typeof d.nombre === 'string' && typeof d.mensaje === 'string')) return null
    return { sitio: e.sitio, medios: e.medios, avisos: e.avisos.filter((a): a is string => typeof a === 'string'), documentos }
  } catch {
    return null
  }
}

export const guardarEspera = (espacio: number, espera: AltaEnEspera): void => {
  try {
    sessionStorage.setItem(claveDeLaEspera(espacio), JSON.stringify(espera))
  } catch {
    /* sin almacenamiento, vale para esta visita */
  }
}

export const olvidarEspera = (espacio: number): void => {
  try {
    sessionStorage.removeItem(claveDeLaEspera(espacio))
  } catch {
    /* nada que olvidar */
  }
}

export const MAXIMO_DE_DOCUMENTOS = 3

const clave = (espacio: number) => `wixy-alta-${espacio}`

/**
 * Cuánto se guarda lo escrito en el asistente. En `localStorage`, para que sobreviva a cerrar
 * la pestaña (auditoría del panel, 29-09-2026, A3), pero no para siempre: pasados unos días, un
 * asistente a medias es más un estorbo que una ayuda.
 */
export const DIAS_QUE_SE_GUARDA = 3

const completar = (datos: Partial<Datos>): Datos => ({
  ...DATOS_INICIALES,
  ...datos,
  marca: { ...DATOS_INICIALES.marca, ...(datos.marca ?? {}) },
  contacto: { ...DATOS_INICIALES.contacto, ...(datos.contacto ?? {}) },
  fiscal: { ...DATOS_INICIALES.fiscal, ...(datos.fiscal ?? {}) },
  tienda: { ...DATOS_INICIALES.tienda, ...(datos.tienda ?? {}) },
})

export const leerGuardado = (espacio: number, ahora = Date.now()): Datos | null => {
  try {
    const crudo = localStorage.getItem(clave(espacio))
    if (crudo) {
      const guardado = JSON.parse(crudo) as { datos?: Partial<Datos>; guardadoEn?: number }
      if (guardado?.datos && typeof guardado.guardadoEn === 'number' && ahora - guardado.guardadoEn <= DIAS_QUE_SE_GUARDA * 24 * 60 * 60 * 1000) return completar(guardado.datos)
      localStorage.removeItem(clave(espacio))
    }
  } catch {
    /* sin almacenamiento, o lo guardado no se entiende: se empieza de cero */
  }
  // Lo que guardaban las versiones anteriores del asistente, en la pestaña.
  try {
    const crudo = sessionStorage.getItem(clave(espacio))
    return crudo ? completar(JSON.parse(crudo) as Partial<Datos>) : null
  } catch {
    return null
  }
}

export const guardar = (espacio: number, datos: Datos, ahora = Date.now()): void => {
  try {
    localStorage.setItem(clave(espacio), JSON.stringify({ datos, guardadoEn: ahora }))
  } catch {
    /* sin almacenamiento, vale para esta visita */
  }
}

export const olvidar = (espacio: number): void => {
  for (const almacen of [() => localStorage, () => sessionStorage]) {
    try {
      almacen().removeItem(clave(espacio))
    } catch {
      /* nada que olvidar */
    }
  }
}

/**
 * Lo guardado viene de una visita anterior que ya había elegido tipo y plantilla: si eligió
 * ficheros, se han perdido (el navegador no los guarda), y hay que decírselo.
 */
export const yaHabiaEmpezado = (datos: Datos): boolean => datos.tipo !== null && datos.plantilla !== null

/** «Ferretería El Sol» → «ferreteria-el-sol». Lo mismo que hace el núcleo con los slugs. */
export const slugDe = (texto: string): string =>
  texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63)

/** El `alt` que se propone para una imagen, a partir del nombre del fichero: se puede corregir. */
export const altDe = (fichero: File): string => fichero.name.replace(/\.[a-z0-9]+$/i, '').replace(/[-_]+/g, ' ').trim()

export const MONEDAS = [
  { valor: 'PEN', nombre: 'Sol peruano (S/)' },
  { valor: 'USD', nombre: 'Dólar ($)' },
  { valor: 'EUR', nombre: 'Euro (€)' },
  { valor: 'MXN', nombre: 'Peso mexicano' },
  { valor: 'COP', nombre: 'Peso colombiano' },
  { valor: 'CLP', nombre: 'Peso chileno' },
  { valor: 'ARS', nombre: 'Peso argentino' },
]

export const TIPOGRAFIAS: Array<{ valor: Datos['marca']['tipografia']; nombre: string; descripcion: string; muestra: string }> = [
  { valor: 'sistema', nombre: 'Sistema', descripcion: 'Limpia y rápida: la letra del dispositivo de cada visitante.', muestra: 'font-sans' },
  { valor: 'clasica', nombre: 'Clásica', descripcion: 'Títulos con serif, para transmitir oficio y confianza.', muestra: 'font-serif' },
  { valor: 'moderna', nombre: 'Moderna', descripcion: 'Títulos compactos y contundentes.', muestra: 'font-sans tracking-tight' },
]
